import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, LogOut } from "lucide-react";
import { BRAND } from "@/config/brand";
import LoginModal from "@/components/LoginModal";

// Sprint 4 #223 — Navbar je nach Auth-Status unterschiedlich.
// Anonym sieht: Selbsttest + Preise + Anmelden (kein Dashboard/Co-Creator-Link).
// Authentifiziert sieht: Dashboard + Anliegen + Abo + Logout.
//
// Sprint 6 HOCH 4 — „Anmelden" öffnet jetzt Modal-Dialog (kein /login-Page-Redirect).
// Sprint 6 HOCH 6 AC #2 — Top-Nav für eingeloggte User um „Abo" erweitert.
const NAV_LINKS_ANON = [
  { to: "/self-test", label: "Selbsttest", testid: "nav-selbsttest" },
  { to: "/pricing", label: "Preise", testid: "nav-pricing" },
];
// Sprint M5.40 (30.07.2026) — Block A1/B5:
// „Mein Plan" nur für Pro-User. Free-User sehen nur „Preise" im Top-Header.
const NAV_LINKS_AUTHED_FREE = [
  { to: "/pricing", label: "Preise", testid: "nav-pricing" },
];
const NAV_LINKS_AUTHED_PRO = [
  { to: "/dashboard/abo", label: "Mein Plan", testid: "nav-plan" },
  { to: "/pricing", label: "Preise", testid: "nav-pricing" },
];

export default function Header({ hideAnonAuthActions = false, variant = "default", hideLogoOnPage = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAuthed = user && user !== false;
  // Sprint 12+M5.17-p (22.07.2026) — Pionier-Header-Variante.
  // Fuer eingeloggte Pioniere blenden wir Anmelden/Registrieren komplett
  // aus und zeigen stattdessen "Mein PocketCoach" + "Abmelden".
  //
  // Sprint 12+M5.17-r (22.07.2026) — Post-Submit-Context-Fix.
  // `hideAnonAuthActions` blendet fuer anonyme User auch die letzten
  // Auth-CTAs weg — genutzt im STATE_HINT nach Form-Submit.
  //
  // Sprint 12+M5.17-s (22.07.2026) — Pionier-Onboarding-Slim-Variant.
  // `variant="pionier"` reduziert die Nav auf das Minimum, damit Pionier-
  // Interessenten NICHT versehentlich in die Free-/Pro-Schiene abbiegen:
  //   - Nav-Links (Selbsttest, Preise, Dashboard, ...) werden komplett
  //     unterdrueckt
  //   - "Registrieren" wird ausgeblendet (Pionier laeuft ueber /warteliste-
  //     Opt-in, nicht ueber generischen Register-Flow)
  //   - "Anmelden" bleibt sichtbar UND wird prominenter (outlined Button
  //     statt Text-Link) — fuer Rueckkehrer sichtbarer
  //   - Cockpit/Abmelden fuer eingeloggte Pioniere unveraendert
  //
  // Sprint-V24 Mini-Fix (11.02.2026, Boardroom-CoS) — `variant="login"`
  // fuer /login-Seite: Blendet Nav-Links (Selbsttest, Preise) UND den
  // „Anmelden"-CTA aus (Redundanz, wer auf /login ist braucht keinen
  // „Anmelden"-Link mehr). „Registrieren" bleibt fuer Wechsler sichtbar.
  const isPionier = isAuthed && (user.is_pionier === true);
  const isPionierVariant = variant === "pionier";
  const isLoginVariant = variant === "login";
  // Sprint M5.40 (30.07.2026): Free-User sehen im Top-Header NUR „Preise",
  // Pro-User zusätzlich „Mein Plan".
  const licenseTier = isAuthed ? (user.license_tier || "free") : "free";
  const isProLike = licenseTier && licenseTier !== "free";
  const navLinks = (isPionierVariant || isLoginVariant)
    ? []
    : (isAuthed
        ? (isProLike ? NAV_LINKS_AUTHED_PRO : NAV_LINKS_AUTHED_FREE)
        : NAV_LINKS_ANON);
  // Sprint 7 (Slim) — Zwei separate Modal-States:
  //   loginOpen  → mode="login"   (Magic-Link für bestehende User)
  //   optinOpen  → mode="optin"   (DOI-Registrierung mit Vorname + DSGVO)
  const [loginOpen, setLoginOpen] = useState(false);
  const [optinOpen, setOptinOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `relative text-sm font-semibold transition-colors duration-200 px-1 py-2 ${
      isActive
        ? "text-[#1E40AF]"
        : "text-slate-600 hover:text-slate-900"
    }`;

  return (
    <header
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        {hideLogoOnPage ? (
          /* Sprint 12+M5.19 (24.07.2026) — Wenn die Seite ihr eigenes
              Content-Logo zeigt (z. B. WartelistePage, PionierDanke),
              blenden wir das Header-Wordmark aus. Spacer-Div haelt die
              rechte Action-Gruppe im justify-between-Layout rechts. */
          <div aria-hidden="true" data-testid="header-logo-hidden-spacer" />
        ) : (
          /* Sprint M5.36 (29.07.2026) — Issue 6: Eingeloggte User landen beim
             Logo-Klick auf ihrem passenden Cockpit (Pionier → /dashboard/pionier,
             sonst /cockpit), nicht mehr auf der Landingpage/Login. */
          <Link
            to={isAuthed ? (isPionier ? "/dashboard/pionier" : "/cockpit") : "/"}
            className="flex items-center group"
            data-testid="logo-link"
          >
            <img
              src="/brand/flex4success-wordmark.png"
              alt={BRAND.name}
              data-testid="header-brand-wordmark"
              className="h-9 sm:h-10 w-auto select-none transition-opacity group-hover:opacity-80"
              draggable="false"
            />
          </Link>
        )}

        <nav
          className="hidden md:flex items-center gap-8"
          data-testid="main-nav"
        >
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testid}
              className={linkClass}
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span
                      className="absolute left-0 right-0 -bottom-[21px] h-[2px] bg-[#1E40AF] rounded-full"
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}

          {isPionier ? (
            <div className="flex items-center gap-2">
              <Link
                to="/dashboard/pionier"
                data-testid="header-cockpit-link"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#4563BB] hover:bg-[#3a54a3] rounded-lg px-4 py-2 transition-colors"
              >
                <LayoutDashboard className="w-4 h-4" />
                Mein PocketCoach
              </Link>
              <Button
                onClick={handleLogout}
                data-testid="header-logout-btn"
                variant="ghost"
                className="rounded-xl text-slate-600 hover:text-slate-900 font-semibold h-9 px-3"
              >
                <LogOut className="w-4 h-4 mr-1.5" />
                Abmelden
              </Button>
            </div>
          ) : isAuthed ? (
            <Button
              onClick={handleLogout}
              data-testid="header-logout-btn"
              variant="ghost"
              className="rounded-xl text-slate-600 hover:text-slate-900 font-semibold h-9 px-3"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Logout
            </Button>
          ) : (
            !hideAnonAuthActions && (
              isPionierVariant ? (
                <button
                  type="button"
                  onClick={() => setLoginOpen(true)}
                  data-testid="nav-login"
                  className="text-sm font-semibold text-[#4563BB] hover:text-white hover:bg-[#4563BB] border-2 border-[#4563BB] rounded-lg px-4 py-2 transition-colors"
                >
                  Anmelden
                </button>
              ) : isLoginVariant ? (
                /* Sprint-V24 Mini-Fix (11.02.2026): auf /login KEIN Anmelden-Link
                   (redundant), nur „Registrieren" als Wechsel-CTA fuer Neu-User. */
                <button
                  type="button"
                  onClick={() => setOptinOpen(true)}
                  data-testid="nav-register"
                  className="text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2 transition-colors"
                >
                  Registrieren
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setLoginOpen(true)}
                    data-testid="nav-login"
                    className="text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors px-3 py-2"
                  >
                    Anmelden
                  </button>
                  <button
                    type="button"
                    onClick={() => setOptinOpen(true)}
                    data-testid="nav-register"
                    className="text-sm font-bold text-white bg-[var(--fts-accent,#DC2626)] hover:opacity-90 rounded-lg px-4 py-2 transition-opacity"
                  >
                    Registrieren
                  </button>
                </div>
              )
            )
          )}
        </nav>

        {/* Mobile fallback */}
        <div className="flex md:hidden items-center gap-3">
          {isPionier ? (
            <>
              <Link
                to="/dashboard/pionier"
                data-testid="header-cockpit-link-mobile"
                className="inline-flex items-center gap-1 text-sm font-bold text-white bg-[#4563BB] hover:bg-[#3a54a3] rounded-lg px-3 py-1.5"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                PocketCoach
              </Link>
              <button
                onClick={handleLogout}
                data-testid="header-logout-btn-mobile"
                className="text-sm font-semibold text-slate-700 hover:text-slate-900"
              >
                Abmelden
              </button>
            </>
          ) : isAuthed ? (
            <button
              onClick={handleLogout}
              data-testid="header-logout-btn-mobile"
              className="text-sm font-semibold text-slate-700 hover:text-slate-900"
            >
              Logout
            </button>
          ) : (
            !hideAnonAuthActions && (
              isPionierVariant ? (
                <button
                  type="button"
                  onClick={() => setLoginOpen(true)}
                  data-testid="nav-login-mobile"
                  className="text-sm font-semibold text-[#4563BB] hover:text-white hover:bg-[#4563BB] border-2 border-[#4563BB] rounded-lg px-3 py-1.5"
                >
                  Anmelden
                </button>
              ) : isLoginVariant ? (
                /* Sprint-V24 Mini-Fix (11.02.2026): auf /login KEIN Anmelden-Link
                   (redundant), nur „Registrieren" als Wechsel-CTA fuer Neu-User. */
                <button
                  type="button"
                  onClick={() => setOptinOpen(true)}
                  data-testid="nav-register-mobile"
                  className="text-sm font-bold text-white bg-[var(--fts-accent,#DC2626)] hover:opacity-90 rounded-lg px-3 py-1.5"
                >
                  Registrieren
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setLoginOpen(true)}
                    data-testid="nav-login-mobile"
                    className="text-sm font-semibold text-slate-700 hover:text-slate-900"
                  >
                    Anmelden
                  </button>
                  <button
                    type="button"
                    onClick={() => setOptinOpen(true)}
                    data-testid="nav-register-mobile"
                    className="text-sm font-bold text-white bg-[var(--fts-accent,#DC2626)] hover:opacity-90 rounded-lg px-3 py-1.5"
                  >
                    Registrieren
                  </button>
                </>
              )
            )
          )}
        </div>
      </div>
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        mode="login"
      />
      <LoginModal
        open={optinOpen}
        onClose={() => setOptinOpen(false)}
        mode="optin"
      />
    </header>
  );
}
